<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { nextTick } from 'vue';
import axios from 'axios';
import FileUpload from 'vue-upload-component';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import { emitter } from 'shared/helpers/mitt';
import PipelineAPI from '../../../../../../api/pipelineCards';
import { useConfig } from 'dashboard/composables/useConfig';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  notes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['itemUpdated', 'update:notes']);

const { t, locale } = useI18n();
const store = useStore();
const { isStacklab } = useConfig();

const MAX_NOTE_LENGTH = 2000;
const currentNote = ref('');
const savingNotes = ref(false);

// Draft persistence: keep an unsent note across navigation/reload, per card.
// The watch on currentNote (below) saves on change and clears when emptied
// (addNote sets currentNote to '' on success, which clears the draft).
const noteDraftKey = computed(() => `pipeline-note-draft-${props.item?.id}`);

const persistNoteDraft = value => {
  try {
    if (value) localStorage.setItem(noteDraftKey.value, value);
    else localStorage.removeItem(noteDraftKey.value);
  } catch {
    // localStorage may be unavailable (private mode/quota) — drafts are best-effort.
  }
};

watch(currentNote, persistNoteDraft);

onMounted(() => {
  try {
    const draft = localStorage.getItem(noteDraftKey.value);
    if (draft && !currentNote.value) currentNote.value = draft;
  } catch {
    // ignore storage read errors
  }
});
const editingNoteId = ref(null);
const noteAttachments = ref([]);
const isUploadingAttachment = ref(false);
const selectedFileName = ref('');

const currentUser = computed(() => store.getters.getCurrentUser);

const getUserLocale = () => {
  return (locale.value || navigator.language || 'en-US').replace('_', '-');
};

const formatDate = date => {
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat(getUserLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  } catch {
    return '';
  }
};

// Build a new activity entry. Pure helper — does NOT call the backend.
// The activity is appended to item_details.activities in the same atomic
// updateCard request that persists the note (avoids the race condition
// where a second updateCard with stale props.item.item_details overwrites
// the note we just saved).
const buildActivity = (type, details) => ({
  id: Date.now() + Math.random(),
  type,
  details,
  created_at: new Date().toISOString(),
  user: {
    id: currentUser.value.id,
    name: currentUser.value.name,
    avatar_url: currentUser.value.avatar_url,
  },
});

const addNote = async () => {
  if (!currentNote.value.trim() && !noteAttachments.value.length) return;

  try {
    savingNotes.value = true;

    let updatedNotes;
    if (editingNoteId.value) {
      updatedNotes = props.notes.map(note => {
        if (note.id === editingNoteId.value) {
          return {
            ...note,
            text: currentNote.value,
            attachments: [
              ...(note.attachments || []),
              ...noteAttachments.value,
            ],
            updated_at: new Date().toISOString(),
            author: currentUser.value.name,
            author_id: currentUser.value.id,
            author_avatar: currentUser.value.avatar_url,
          };
        }
        return note;
      });
    } else {
      const newNote = {
        id: Date.now(),
        text: currentNote.value,
        created_at: new Date().toISOString(),
        attachments: noteAttachments.value,
        author: currentUser.value.name,
        author_id: currentUser.value.id,
        author_avatar: currentUser.value.avatar_url,
      };
      updatedNotes = [...(props.item.item_details?.notes || []), newNote];
    }

    const activity = buildActivity(
      editingNoteId.value ? 'note_edited' : 'note_added',
      {
        note_text: currentNote.value.substring(0, 100),
        has_attachments: noteAttachments.value.length > 0,
      }
    );
    const updatedActivities = [
      ...(props.item.item_details?.activities ||
        props.item.item_details?.history ||
        []),
      activity,
    ];

    // Send ONLY the delta. Backend does deep_merge from the DB row,
    // so spreading `...props.item.item_details` would resurrect stale
    // values for OTHER keys (priority, checklist, etc) that another
    // tab/handler may have just modified.
    const payload = {
      item_details: {
        notes: updatedNotes,
        activities: updatedActivities,
      },
    };

    const { data } = await PipelineAPI.updateCard(props.item.id, payload);
    emit('update:notes', data.item_details?.notes || updatedNotes);
    emit('itemUpdated');

    currentNote.value = '';
    noteAttachments.value = [];
    selectedFileName.value = '';
    editingNoteId.value = null;

    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.NOTE_ADDED_SUCCESS'),
      action: { type: 'success' },
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[PipelineNotes] addNote failed', error);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.NOTE_ADD_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    savingNotes.value = false;
  }
};

const editNote = note => {
  editingNoteId.value = note.id;
  currentNote.value = note.text;
  nextTick(() => {
    const textarea = document.querySelector('textarea[placeholder]');
    if (textarea) textarea.focus();
  });
};

const deleteNote = async noteId => {
  try {
    const updatedNotes = props.notes.filter(note => note.id !== noteId);
    const activity = buildActivity('note_deleted', { note_id: noteId });
    const updatedActivities = [
      ...(props.item.item_details?.activities ||
        props.item.item_details?.history ||
        []),
      activity,
    ];
    // Delta-only update — backend deep_merges. Avoid spreading
    // props.item.item_details (would clobber other tabs' state).
    const payload = {
      item_details: {
        notes: updatedNotes,
        activities: updatedActivities,
      },
    };
    const { data } = await PipelineAPI.updateCard(props.item.id, payload);
    emit('update:notes', data.item_details?.notes || updatedNotes);
    emit('itemUpdated');
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[PipelineNotes] deleteNote failed', error);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.NOTE_ADD_ERROR'),
      action: { type: 'error' },
    });
  }
};

const handleNoteAttachment = async file => {
  if (!file) return;

  selectedFileName.value = file.file.name;

  if (!props.item?.id) {
    return;
  }

  isUploadingAttachment.value = true;
  try {
    if (checkFileSizeLimit(file, ALLOWED_FILE_TYPES)) {
      const formData = new FormData();
      formData.append('attachment', file.file);

      const url = `/api/v1/accounts/${store.getters.getCurrentAccountId}/pipeline/cards/${props.item.id}/note_attachments`;

      const response = await axios.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const newAttachment = {
        id: response.data.id.toString(),
        url: response.data.attachment_url,
        filename: file.file.name,
        fileType: file.file.type || 'application/octet-stream',
        source: {
          type: 'note',
          id: Date.now(),
        },
      };

      noteAttachments.value = [...noteAttachments.value, newAttachment];
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[PipelineNotes] handleNoteAttachment failed', error);
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGES.NOTE_ADD_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    isUploadingAttachment.value = false;
  }
};
</script>

<template>
  <div class="space-y-4">
    <h3 class="text-heading-3 text-n-slate-12 mb-4">
      {{ t('PIPELINE.NOTES_LABEL') }}
    </h3>

    <!-- New note input -->
    <div class="space-y-3">
      <textarea
        v-model="currentNote"
        data-pipeline-notes-input
        :maxlength="MAX_NOTE_LENGTH"
        class="w-full px-4 py-3 text-sm border border-n-weak bg-n-solid-3 rounded-lg focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand resize-none text-n-slate-12 placeholder-n-slate-9"
        :placeholder="t('PIPELINE.NOTES_PLACEHOLDER')"
        rows="3"
      />

      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span
            class="text-xs tabular-nums"
            :class="
              currentNote.length >= MAX_NOTE_LENGTH
                ? 'text-n-ruby-11 font-semibold'
                : 'text-n-slate-10'
            "
          >
            {{ `${currentNote.length}/${MAX_NOTE_LENGTH}` }}
          </span>
          <FileUpload
            v-if="isStacklab === true"
            :accept="ALLOWED_FILE_TYPES"
            @input-file="handleNoteAttachment"
          >
            <button class="p-2 rounded-lg hover:bg-n-alpha-1 text-n-slate-10">
              <fluent-icon icon="attach" size="18" />
            </button>
          </FileUpload>
        </div>

        <button
          class="px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!currentNote.trim()"
          @click="addNote"
        >
          {{ t('PIPELINE.ADD') }}
        </button>
      </div>
    </div>

    <!-- Notes list -->
    <div class="space-y-3 mt-6 max-h-[calc(100vh-450px)] overflow-y-auto pr-2">
      <div
        v-for="note in notes"
        :key="note.id"
        class="p-4 bg-n-solid-3 rounded-lg"
      >
        <div class="flex items-start justify-between">
          <div class="flex-1">
            <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
              {{ note.text }}
            </p>
            <div class="flex items-center gap-2 mt-2 text-xs text-n-slate-10">
              <span>{{ note.author }}</span>
              <span>&bull;</span>
              <span>{{ formatDate(note.created_at) }}</span>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <button
              class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-10 hover:text-n-slate-12"
              @click="editNote(note)"
            >
              <fluent-icon icon="edit" size="14" />
            </button>
            <button
              class="p-1 rounded hover:bg-n-alpha-1 text-n-slate-10 hover:text-ruby-11"
              @click="deleteNote(note.id)"
            >
              <fluent-icon icon="delete" size="14" />
            </button>
          </div>
        </div>
      </div>

      <div v-if="notes.length === 0" class="text-center py-8">
        <fluent-icon
          icon="document"
          size="32"
          class="text-n-slate-9 mx-auto mb-2"
        />
        <p class="text-sm text-n-slate-10">
          {{ t('PIPELINE.NOTES_EMPTY') }}
        </p>
      </div>
    </div>
  </div>
</template>
